// Keyboard control of the wall board (WCAG 2.1.1). Keys on the canvas pan and zoom; on a note, Space toggles its
// selection and arrows move the selected notes. Arrows on an unselected note bubble up and pan, so moving a note
// always needs the deliberate step of selecting it first.
import { nextTick, Ref, ref } from 'vue'
import useNoteSelection from '@/composables/noteSelection'

const PAN_STEP = 0.1 // share of the visible canvas
const PAN_STEP_LARGE = 0.5
const MOVE_STEP = 0.5 // percent of the board, the unit note positions are stored in
const MOVE_STEP_LARGE = 2.5
const ZOOM_STEP = 0.25 // same steps as the zoom buttons in SidebarControls
const KEEP_IN_VIEW_MARGIN = 24
const ANNOUNCE_DELAY_MS = 500

const arrowDirections: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
}

export interface BoardKeyboardOptions {
  canvas: Ref<HTMLElement | undefined>
  translateX: Readonly<Ref<number>>
  translateY: Readonly<Ref<number>>
  scale: Readonly<Ref<number>>
  panTo: (x: number, y: number) => void
  zoomToScale: (scale: number) => void
  resetZoom: () => void
  // Moves every selected note by the given percent of the board and returns how many moved
  moveSelection: (dxPercent: number, dyPercent: number) => number
  t: (key: string, values: Record<string, unknown>) => string
}

export function useBoardKeyboard(options: BoardKeyboardOptions) {
  const selection = useNoteSelection()
  const announcement = ref('')
  let announceTimer: ReturnType<typeof setTimeout> | undefined

  function announce(text: string, delay = 0) {
    clearTimeout(announceTimer)
    announceTimer = setTimeout(() => {
      // Clearing first makes screen readers repeat an identical message
      announcement.value = ''
      nextTick(() => (announcement.value = text))
    }, delay)
  }

  function isTextEntry(target: EventTarget | null) {
    return target instanceof HTMLElement && !!target.closest('input, textarea, select, [contenteditable="true"]')
  }

  function pan(dx: number, dy: number, large: boolean) {
    const canvas = options.canvas.value
    if (!canvas) return
    const step = large ? PAN_STEP_LARGE : PAN_STEP
    // Moving the view right means moving the content left
    options.panTo(options.translateX.value - dx * canvas.clientWidth * step, options.translateY.value - dy * canvas.clientHeight * step)
  }

  function keepInView(el: HTMLElement) {
    const canvas = options.canvas.value
    if (!canvas) return
    const c = canvas.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    let dx = 0
    let dy = 0
    if (r.left < c.left + KEEP_IN_VIEW_MARGIN) dx = c.left + KEEP_IN_VIEW_MARGIN - r.left
    else if (r.right > c.right - KEEP_IN_VIEW_MARGIN) dx = c.right - KEEP_IN_VIEW_MARGIN - r.right
    if (r.top < c.top + KEEP_IN_VIEW_MARGIN) dy = c.top + KEEP_IN_VIEW_MARGIN - r.top
    else if (r.bottom > c.bottom - KEEP_IN_VIEW_MARGIN) dy = c.bottom - KEEP_IN_VIEW_MARGIN - r.bottom
    if (dx || dy) options.panTo(options.translateX.value + dx, options.translateY.value + dy)
  }

  // Handles keys from the canvas and keys that bubble up from notes
  function onCanvasKeydown(e: KeyboardEvent) {
    // Leave browser and OS shortcuts (e.g. Cmd + / Cmd - page zoom) alone
    if (e.ctrlKey || e.metaKey || e.altKey || isTextEntry(e.target)) return

    const direction = arrowDirections[e.key]
    if (direction) {
      pan(direction[0], direction[1], e.shiftKey)
    } else if (e.key === '+' || e.key === '=') {
      options.zoomToScale(Math.floor(options.scale.value / ZOOM_STEP) * ZOOM_STEP + ZOOM_STEP)
    } else if (e.key === '-') {
      options.zoomToScale(Math.floor(options.scale.value / ZOOM_STEP) * ZOOM_STEP - ZOOM_STEP)
    } else if (e.key === '0') {
      options.resetZoom()
    } else if (e.key === 'Escape' && selection.getters.selectedCount.value > 0) {
      selection.actions.clearSelection()
      announce(options.t('selectionCleared', {}))
    } else return
    e.preventDefault()
  }

  function onNoteKeydown(e: KeyboardEvent, noteId: string) {
    // Keys inside the note (its text editor and buttons) belong to those controls
    if (e.target !== e.currentTarget || e.ctrlKey || e.metaKey || e.altKey) return

    if (e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      selection.actions.toggleNote(noteId)
      const n = selection.getters.selectedCount.value
      announce(options.t(selection.getters.isSelected(noteId) ? 'noteSelected' : 'noteDeselected', { n }))
      return
    }

    const direction = arrowDirections[e.key]
    if (!direction || !selection.getters.isSelected(noteId)) return // bubbles to the canvas and pans
    e.preventDefault()
    e.stopPropagation()
    const step = e.shiftKey ? MOVE_STEP_LARGE : MOVE_STEP
    const moved = options.moveSelection(direction[0] * step, direction[1] * step)
    const el = e.currentTarget as HTMLElement
    nextTick(() => keepInView(el))
    if (moved) announce(options.t('notesMoved', { n: moved }), ANNOUNCE_DELAY_MS)
  }

  return { announcement, onCanvasKeydown, onNoteKeydown }
}
